import * as React from 'react';
import currency from 'currency.js';
import { camelCase, capitalize, isNil, snakeCase } from 'lodash';
import classnames from 'classnames';

import CampaignHistoryCardPreview from '../campaign-history-card-preview/CampaignHistoryCardPreview';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import CreatorTilePriceReco from 'components/creator-tile/price-reco/CreatorTilePriceReco';
import ImageGrid from 'components/image-grid/ImageGrid';
import ActionLink from 'components/buttons/action-link/ActionLink';
import PageSection from 'components/page/page-section/PageSection';
import Tooltip from 'components/Tooltip';
import VerticalTimeline from 'components/timeline/VerticalTimeline';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { MetricsChannelsBasicItem } from 'components/metrics/channels-basic/types';
import MetricsChannelsBasicSingleEntry from 'components/metrics/channels-basic/MetricsChannelsBasicSingleEntry';

import { getEntityFromProfile } from 'utils/view';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, getVisibleAttachmentsFromDeliverable } from 'utils/channels';
import {
  DELIVERABLE_ICONS,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT
} from 'utils/deliverables';
import { abbrNum } from 'utils/helper/common';
import { getCountMetric, getMoneyMetric, getRateMetric, getTimeMetric } from 'utils/metrics';
import { renderPrice } from 'utils/helper/price';

import type {
  ChannelTypes,
  Deliverable,
  DeliverableWithAttachments,
  InfluencerBindingAgreement,
  InfluencerCampaignHistoryEvent,
  InfluencerCampaignHistoryItem,
  Kpi
} from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-history-card.i18n.json'));

const deliverablesTypesWithAttachments = [
  DELIVERABLE_TYPE_YOUTUBE_VIDEO,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL
];

const KPI_CHANNELS = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK];

type Props = {
  item: InfluencerCampaignHistoryItem,
  events: Array<InfluencerCampaignHistoryEvent>,
  hideCcmCard: boolean,
  disabled?: boolean,
  disabledMessenger?: boolean
};

type State = {
  isContentVisible: boolean
};

const TASK_METRICS = {
  instagram_post: [{
    key: 'likes',
    label: __t('campaign_history_card.analytic_labels.likes'),
    displayMetricFn: getCountMetric
  }, {
    key: 'comments',
    label: __t('campaign_history_card.analytic_labels.comments'),
    displayMetricFn: getCountMetric
  }, {
    key: 'impressions',
    label: __t('campaign_history_card.analytic_labels.impressions'),
    displayMetricFn: getCountMetric
  }, {
    key: 'reach',
    label: __t('campaign_history_card.analytic_labels.reach'),
    displayMetricFn: getCountMetric
  }, {
    key: 'reactions',
    label: __t('campaign_history_card.analytic_labels.reactions'),
    displayMetricFn: getCountMetric
  }, {
    key: 'mediaValue',
    label: __t('campaign_history_card.analytic_labels.media_value'),
    displayMetricFn: getMoneyMetric,
    isMoneyMetric: true
  }, {
    key: 'instagramEngagement',
    label: __t('campaign_history_card.analytic_labels.engagement_rate'),
    displayMetricFn: getRateMetric
  }],
  instagram_story: [{
    key: 'views',
    label: __t('campaign_history_card.analytic_labels.views'),
    displayMetricFn: getCountMetric
  }, {
    key: 'impressions',
    label: __t('campaign_history_card.analytic_labels.impressions'),
    displayMetricFn: getCountMetric
  }, {
    key: 'reach',
    label: __t('campaign_history_card.analytic_labels.reach'),
    displayMetricFn: getCountMetric
  }, {
    key: 'mediaValue',
    label: __t('campaign_history_card.analytic_labels.media_value'),
    displayMetricFn: getMoneyMetric,
    isMoneyMetric: true
  }],
  instagram_reel: [{
    key: 'reach',
    label: __t('campaign_history_card.analytic_labels.reach'),
    displayMetricFn: getCountMetric
  }, {
    key: 'reactions',
    label: __t('campaign_history_card.analytic_labels.tiktok_reactions'),
    displayMetricFn: getCountMetric
  }, {
    key: 'tiktokEngagementRatePerFollower',
    label: __t('campaign_history_card.analytic_labels.tiktok_engagement_rate_per_follower'),
    displayMetricFn: getRateMetric
  }, {
    key: 'tiktokEngagementRatePerView',
    label: __t('campaign_history_card.analytic_labels.tiktok_engagement_rate_per_view'),
    displayMetricFn: getRateMetric
  }, {
    key: 'mediaValue',
    label: __t('campaign_history_card.analytic_labels.media_value'),
    displayMetricFn: getMoneyMetric,
    isMoneyMetric: true
  }],
  tiktok_video: [{
    key: 'tiktokWatchRate',
    label: __t('campaign_history_card.analytic_labels.tiktok_watch_rate')
  }, {
    key: 'tiktokAverageWatchTime',
    label: __t('campaign_history_card.analytic_labels.tiktok_average_watch_time'),
    displayMetricFn: getTimeMetric
  }, {
    key: 'reach',
    label: __t('campaign_history_card.analytic_labels.reach'),
    displayMetricFn: getCountMetric
  }, {
    key: 'reactions',
    label: __t('campaign_history_card.analytic_labels.tiktok_reactions'),
    displayMetricFn: getCountMetric
  }, {
    key: 'tiktokEngagementRatePerFollower',
    label: __t('campaign_history_card.analytic_labels.tiktok_engagement_rate_per_follower'),
    displayMetricFn: getRateMetric
  }, {
    key: 'tiktokEngagementRatePerView',
    label: __t('campaign_history_card.analytic_labels.tiktok_engagement_rate_per_view'),
    displayMetricFn: getRateMetric
  }, {
    key: 'mediaValue',
    label: __t('campaign_history_card.analytic_labels.media_value'),
    displayMetricFn: getMoneyMetric,
    isMoneyMetric: true
  }]
};

const COMMON_MSG_KEY = 'collabary_common';

class CampaignHistoryCard extends React.PureComponent<Props, State> {
  state = {
    isContentVisible: false
  };

  getIsCcmCampaignStatus = (): boolean => (
    this.props.item.campaign.campaignType === 'ccm'
  );

  toggleContentVisibility = () => {
    this.setState(state => ({
      isContentVisible: !state.isContentVisible
    }));
  };

  getTotalAmount(tasks: Array<InfluencerBindingAgreement>): number {
    return tasks.reduce((prevValue, task) => prevValue.add(currency(task.priceTotal || 0)), currency(0)).value;
  }

  getMetricToDisplay(metric: {
    displayMetricFn: (a: number | string) => string,
    isMoneyMetric: boolean
  }, metricValue: number | string) {
    if (metric.isMoneyMetric) {
      return renderPrice(metric.displayMetricFn(metricValue));
    }

    return metric.displayMetricFn(metricValue);
  }

  getChannelWiseTasks(bindingAgreement: Array<InfluencerBindingAgreement>): {
    [x: string]: Array<InfluencerBindingAgreement>
  } {
    const tasks: { [channel: string]: Array<InfluencerBindingAgreement> } = {
      instagram: [],
      tiktok: [],
      youtube: [],
      others: []
    };
    bindingAgreement.forEach(task => {
      if (task.type && task.type.includes('instagram')) {
        tasks.instagram.push(task);
        return;
      }

      if (task.type && task.type.includes('tiktok')) {
        tasks.tiktok.push(task);
        return;
      }

      if (task.type && task.type.includes('youtube')) {
        tasks.youtube.push(task);
        return;
      }

      tasks.others.push(task);
    });

    return tasks;
  }

  getDeliverablesWithAttachment(deliverables: Array<Deliverable>) {
    return (deliverables || [])
      .reduce((deliverablesWithAttachments, deliverable) => {
        if (deliverablesTypesWithAttachments.includes(deliverable.deliverableType)) {
          const attachments = getVisibleAttachmentsFromDeliverable(deliverable);

          if (attachments.length) {
            deliverablesWithAttachments.push({
              id: deliverable.id,
              deliverableType: deliverable.deliverableType,
              createdAt: deliverable.createdAt,
              attachments
            });
          }
        }

        return deliverablesWithAttachments;
      }, [] as Array<Partial<Deliverable>>);
  }

  renderContentMetrics(deliverable: Partial<DeliverableWithAttachments>): React.ReactNode {
    const { item: { deliverablesAnalytics } } = this.props;
    const deliverablePerformance = deliverablesAnalytics.find(deliverableItem => deliverableItem.id === deliverable.id);

    if (!deliverablePerformance) {
      return null;
    }

    const metricContainerClasses = classnames({
      'campaign-history-card__content-stat-container': true,
      'campaign-history-card__content-stat-container--wide': (
        deliverable.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO
        || deliverable.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_REEL
      )
    });

    return (
      <div className={metricContainerClasses}>
        <div className="campaign-history-card__task_type">
          {__t(`${COMMON_MSG_KEY}.types.${deliverable.deliverableType}.singular`)}
        </div>
        <div className="campaign-history-card__task_date">
          {getDisplayDate(deliverable.createdAt)}
        </div>
        {
          (TASK_METRICS[deliverable.deliverableType || ''] || []).map(metric => (
            <div className="campaign-history-card__task_metrics" key={metric.key}>
              <span>{metric.label}</span>
              <span className="campaign-history-card__task_metric_value">
                { metric.displayMetricFn ? (this.getMetricToDisplay(metric, deliverablePerformance[metric.key]) || '-') :
                  deliverablePerformance[metric.key] }
              </span>
            </div>
          ))
        }
        {
          deliverablePerformance.tiktokAudienceByCountries && (
            deliverablePerformance.tiktokAudienceByCountries.slice(0, 3).map((countryMetrics, index) => (
              <div className="campaign-history-card__task_metrics" key={countryMetrics.code}>
                <span>{__t('campaign_history_card.analytic_labels.tiktok_audience_by_country', { index: index + 1 })}</span>
                <span className="campaign-history-card__task_metric_value">{`${countryMetrics.code} (${abbrNum(countryMetrics.reach)} | ${countryMetrics.percent}%)`}</span>
              </div>
            ))
          )
        }
      </div>
    );
  }

  renderNegotiationHistory(): React.ReactNode {
    const { events, item: { campaign, subcampaign }, disabled, disabledMessenger } = this.props;
    const isCcmCampaign = this.getIsCcmCampaignStatus();
    const timelineEvents = events.map(event => {
      const isAgreementChanged = event.historyType === 'agreement_change';

      return {
        title: isAgreementChanged
          ? __t(`campaign_history_card.negotiation_history_${event.historyType}_${event.offerState}_title`)
          : __t(`campaign_history_card.negotiation_history_${event.historyType}_title`),
        description: isAgreementChanged
          ? ''
          : __t(`campaign_history_card.negotiation_history_${event.historyType}_type_${event.offerType}_description`, {
            deliverablesCount: event.deliverablesCount,
            tasksLabel: __t('campaign_history_card.tasks_label', { withPlural: event.deliverablesCount }),
            cost: event.cost
          }),
        date: event.date,
        tasksChanges: event.tasksChanges,
        historyType: event.historyType
      };
    });

    return (
      <div className="campaign-history-card__negotiation-history">
        <div className="campaign-history-card__title">
          {__t('campaign_history_card.negotiation_history_title')}
          <div className="campaign-history-card__title-action">
            {!disabled && !disabledMessenger && (
              <ActionLink
                size="normal"
                color="light"
                link={`/campaigns${isCcmCampaign ? '/ccm' : ''}/${campaign.id}/messages?subcampaignId=${subcampaign.id}`}
                openInNewTab
              >
                {__t('campaign_history_card.view_campaign_messages_label')}
              </ActionLink>
            )}
          </div>
        </div>
        <VerticalTimeline events={timelineEvents} />
      </div>
    );
  }

  renderDeliverables(): React.ReactNode {
    const { item: { deliverables, noAgreement, campaign, subcampaign }, disabled } = this.props;
    const deliverableWithAttachments = this.getDeliverablesWithAttachment(deliverables);
    const isCcmCampaign = this.getIsCcmCampaignStatus();

    return !noAgreement && deliverables.length > 0 && (
      <div>
        <div className="campaign-history-card__title">
          {__t('campaign_history_card.deliverables_title')}
          <div className="campaign-history-card__title-action">
            {!disabled && (
              <ActionLink
                size="normal"
                color="light"
                link={`/campaigns${isCcmCampaign ? '/ccm' : ''}/${campaign.id}/deliverables?subcampaignId=${subcampaign.id}`}
                openInNewTab
              >
                {__t('campaign_history_card.view_deliverables_label')}
              </ActionLink>
            )}
          </div>
        </div>
        <div className="campaign-history-card__deliverables">
          {
            deliverableWithAttachments.slice(0, 3)
              .map(deliverable => {
                const tooltipContent = this.renderContentMetrics(deliverable);
                return (
                  <div
                    className="campaign-history-card__deliverable"
                    key={`history-card-deliverable-${deliverable.id}`}
                  >
                    <div>
                      <ImageGrid
                        attachments={deliverable.attachments || []}
                        size={136}
                      />
                    </div>
                    <div className="campaign-history-card__deliverable-footer">
                      <div className="campaign-history-card__deliverable-footer-label">
                        {__t(`${COMMON_MSG_KEY}.types.${snakeCase(deliverable.deliverableType)}.singular`)}
                      </div>
                      <div className="campaign-history-card__deliverable-footer-stats-hover">
                        {
                          tooltipContent && (
                            <Tooltip
                              component={tooltipContent}
                              width="auto-compact"
                            >
                              <SVGDynamicIcon
                                name="equalizer-custom-bg"
                                width={16}
                                height={17}
                                customColor="grey"
                                clickable
                              />
                            </Tooltip>
                          )
                        }
                      </div>
                    </div>
                  </div>
                );
              })
          }
          {
            deliverableWithAttachments.length === 0 && (
              <div className="campaign-history-card__no-data-available">
                {__t('campaign_history_card.no_content_available')}
              </div>
            )
          }
        </div>
      </div>
    );
  }

  renderInfluencerMetricInfo() {
    const { item: { campaign, channels } } = this.props;
    const shownChannels: Array<ChannelTypes> = ['instagram', 'tiktok', 'youtube'];
    const mappedChannelsMetrics = shownChannels
      .reduce<Array<MetricsChannelsBasicItem>>((result, channel) => {
        const channelInfo = channels[channel];
        if (channelInfo) {
          result.push({
            engagementRate: channelInfo.metrics?.engagement,
            followers: channelInfo.metrics?.followers,
            type: channel,
            url: channelInfo.url
          });
        }

        return result;
      }, []);

    return (
      <div className="campaign-history-card__metrics">
        <div className="campaign-history-card__title">
          {__t('campaign_history_card.historical_data_title')}
          <div className="campaign-history-card__campaign-date">
            {`${__t('campaign_history_card.start_date')}: `}
            <span className="campaign-history-card__campaign-start-date">{getDisplayDate(campaign.startDate)}</span>
          </div>
        </div>
        <div className="campaign-history-card__historical-data">
          {
            mappedChannelsMetrics.map(channel => (
              <div
                key={channel.type}
                className="campaign-history-card__channel-historical-data"
              >
                <div className="campaign-history-card__channel-metric">
                  <MetricsChannelsBasicSingleEntry
                    item={channel}
                    showChannels={['instagram', 'tiktok', 'youtube']}
                    layout="block"
                    includeRatio
                  />
                </div>
                <CreatorTilePriceReco
                  className="campaign-history-card__price-reco"
                  caption={(
                    <SVGDynamicIcon
                      name="euro-inside-circle-custom-fg"
                      width={16}
                      height={16}
                      customColor="grey"
                    />
                  )}
                  captionClassName="campaign-history-card__price-reco-caption"
                  channelsData={channels}
                  channels={[channel.type]}
                />
              </div>
            ))
          }
        </div>
      </div>
    );
  }

  renderCampaignPerfomance() {
    const { item: { campaign, channels, hasDefinedAttribution }, disabled } = this.props;
    const isCcmCampaign = this.getIsCcmCampaignStatus();
    let kpis = [] as Array<Kpi>;

    KPI_CHANNELS.forEach(channel => {
      if (channels[channel]?.kpi) {
        kpis = [...kpis, ...channels[channel].kpi];
      }
    });

    if (!hasDefinedAttribution && !isNil(channels.overall)) {
      kpis = [...channels.overall?.kpi ?? []];
    }

    return (
      <div>
        <div className="campaign-history-card__title">
          {__t('campaign_history_card.campaign_perfomance')}
          {
            !hasDefinedAttribution && this.renderCCAButton()
          }
          <div className="campaign-history-card__title-action">
            {!disabled && (
              <ActionLink
                size="normal"
                color="light"
                link={`/campaigns${isCcmCampaign ? '/ccm' : ''}/${campaign.id}/report${!isCcmCampaign ? '/v2' : ''}`}
                openInNewTab
              >
                {__t('campaign_history_card.visit_reporting_label')}
              </ActionLink>
            )}
          </div>
        </div>
        <div>
          <div className="campaign-history-card__kpi-header">
            <div className="campaign-history-card__task-type-title">
              {__t('campaign_history_card.task_type_label')}
            </div>
            <div className="campaign-history-card__kpi-value-header">
              {__t('campaign_history_card.kpi.engagement_label')}
              <span className="campaign-history-card__tooltip">
                <Tooltip
                  message={__t('campaign_history_card.kpi.engagement_tooltip')}
                  showQuestionMark
                />
              </span>
            </div>
            <div className="campaign-history-card__kpi-value-header">
              {__t('campaign_history_card.kpi.cpe_label')}
              <span className="campaign-history-card__tooltip">
                <Tooltip
                  message={__t('campaign_history_card.kpi.cpe_tooltip')}
                  showQuestionMark
                />
              </span>
            </div>
            <div className="campaign-history-card__kpi-value-header">
              {__t('campaign_history_card.kpi.ir_label')}
              <span className="campaign-history-card__tooltip">
                <Tooltip
                  message={__t('campaign_history_card.kpi.ir_tooltip')}
                  showQuestionMark
                />
              </span>
            </div>
            <div className="campaign-history-card__kpi-value-header">
              {__t('campaign_history_card.kpi.cpm_label')}
              <span className="campaign-history-card__tooltip">
                <Tooltip
                  message={__t('campaign_history_card.kpi.cpm_tooltip')}
                  showQuestionMark
                />
              </span>
            </div>
          </div>
          {
            kpis.map(kpi => (
              <div className="campaign-history-card__task-row" key={kpi.kpiType}>
                <div className="campaign-history-card__task-type">
                  {__t(`campaign_history_card.kpi.${kpi.kpiType}`)}
                </div>
                <div className="campaign-history-card__kpi-value">
                  {kpi.engagement}%
                </div>
                <div className="campaign-history-card__kpi-value">
                  {kpi.cpe}€
                </div>
                <div className="campaign-history-card__kpi-value">
                  {kpi.impressionsRate}%
                </div>
                <div className="campaign-history-card__kpi-value">
                  {kpi.cpm}€
                </div>
              </div>
            ))
          }
          {
            !kpis.length && (
              <div className="campaign-history-card__no-data-available">
                {__t('campaign_history_card.no_kpi_data_available')}
              </div>
            )
          }
        </div>
      </div>
    );
  }

  renderCCAButton(): React.ReactNode {
    const { item: { campaign, subcampaign, deliverables } } = this.props;
    const oneNonOutfitTask = deliverables.some(d => d.deliverableType !== DELIVERABLE_TYPE_ZALANDO_OUTFIT);

    if (subcampaign.state == 'accepted' && oneNonOutfitTask) {
      return (
        <ActionButton
          type="secondary"
          link={`/campaigns/${campaign.id}/report/v2/cca#creator-tile-${subcampaign?.creatorId}`}
          newTab
          className="campaign-history-card__cca_btn"
        >
          {__t('campaign_history_card.cca_cta_label')}
        </ActionButton>
      );
    }

    return null;
  }

  renderWarningTooltip(isLeftMargin?: boolean): React.ReactNode {
    const classes = classnames('campaign-history-card__cca-warning',
      {
        'campaign-history-card__cca-warning--margin-left': isLeftMargin
      });

    return (
      <div className={classes}>
        <Tooltip message={__t('campaign_history_card.missing_cca_tooltip')}>
          <SVGDynamicIcon
            height={17}
            width={17}
            customColor="red"
            name="exclamation-inside-circle-custom-bg-white-fg"
          />
        </Tooltip>
      </div>
    );
  }

  private renderAdditionalCosts(item: InfluencerCampaignHistoryItem): React.ReactNode {
    if (!item.additionalCosts) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="campaign-history-card__binding-agreement-task-header">
          <div className="campaign-history-card__task-channel">
            {__t('campaign_history_card.additional_costs_label')}
          </div>
          <div className="campaign-history-card__task-channel-amount">
            {item.additionalCosts} €
          </div>
        </div>
        <div className="campaign-history-card__binding-agreement-task">
          <div className="campaign-history-card__task-type">
            {item.additionalCostsDetail}
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderInfluencerTasks(channelTasks: Array<InfluencerBindingAgreement>, channel: string): React.ReactNode {
    const isMainChannel = channel !== 'others';
    const { item: { hasDefinedAttribution } } = this.props;
    const channelTotalAmount = this.getTotalAmount(channelTasks);

    if (channelTasks.length) {
      return (
        <React.Fragment key={channel}>
          <div className="campaign-history-card__binding-agreement-task-header">
            <div className="campaign-history-card__task-channel">
              {isMainChannel ? capitalize(channel) : 'Other tasks'}
            </div>
            {
              isMainChannel && !!channelTotalAmount && (
                <div className="campaign-history-card__task-channel-amount">
                  {`${this.getTotalAmount(channelTasks)} €`}
                </div>
              )
            }
          </div>
          {
            channelTasks.map((task, index) => {
              if (task.type.includes('instagram') || task.type.includes('tiktok')) {
                return (
                  <div className="campaign-history-card__binding-agreement-task" key={index}>
                    <div className="campaign-history-card__task-type">
                      <div className="campaign-history-card__no-of-task">
                        {task.amount}
                      </div>
                      <div className="campaign-history-card__deliverable-icon">
                        <SVGDynamicIcon
                          name={`${DELIVERABLE_ICONS[camelCase(task.type)]}`}
                          width={16}
                          height={16}
                        />
                      </div>
                      <div className="campaign-history-card__task-type">
                        {__t(`campaign_history_card.${task.type}`)}
                        {
                          !hasDefinedAttribution && (
                            this.renderWarningTooltip(true)
                          )
                        }
                      </div>
                    </div>
                    {
                      hasDefinedAttribution && (
                        <React.Fragment>
                          <div className="campaign-history-card__price-per-task-type">
                            {__t('campaign_history_card.each_task_cost', {
                              val: isNil(task.pricePerEach)
                                ? __t('campaign_history_card.each_task_cost_na')
                                : task.pricePerEach
                            })}
                          </div>
                          <div className="campaign-history-card__type-total-amount">
                            {
                              isNil(task.priceTotal)
                                ? `${__t('campaign_history_card.each_task_cost_na')} €`
                                : `${task.priceTotal} €`
                            }
                          </div>
                        </React.Fragment>
                      )
                    }
                  </div>
                );
              }

              return (
                <div className="campaign-history-card__binding-agreement-task" key={index}>
                  <div className="campaign-history-card__task-type">
                    <div className="campaign-history-card__no-of-task">
                      {task.amount}
                    </div>
                    <div className="campaign-history-card__deliverable-icon">
                      <SVGDynamicIcon
                        name={`${DELIVERABLE_ICONS[camelCase(task.type)]}`}
                        width={16}
                        height={16}
                      />
                    </div>
                    <div className="campaign-history-card__task-type">
                      {__t(`campaign_history_card.${task.type}`)}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </React.Fragment>
      );
    }

    return null;
  }

  renderBindingAgreement() {
    const { item, disabled } = this.props;
    const { campaign, bindingAgreement, subcampaign, hasDefinedAttribution } = item;
    const channelWiseTasks = this.getChannelWiseTasks(bindingAgreement);
    const isCcmCampaign = this.getIsCcmCampaignStatus();

    return (
      <div className="campaign-history-card__binding-agreement-section">
        <div className="campaign-history-card__title">
          {__t('campaign_history_card.binding_agreement')}
          {
            !hasDefinedAttribution && this.renderCCAButton()
          }
          <div className="campaign-history-card__title-action">
            {!disabled && (
              <ActionLink
                size="normal"
                color="light"
                link={`/campaigns${isCcmCampaign ? '/ccm' : ''}/${campaign.id}/subcampaigns/${subcampaign.id}`}
                openInNewTab
              >
                {__t('campaign_history_card.view_individual_offer_label')}
              </ActionLink>
            )}
          </div>
        </div>
        <div className="campaign-history-card__binding-agreement-container">
          {
            Object.keys(channelWiseTasks).map(channel => this.renderInfluencerTasks(channelWiseTasks[channel], channel))
          }
          {
            this.renderAdditionalCosts(item)
          }
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { item, disabled, hideCcmCard } = this.props;
    const { campaign } = item;
    const { isContentVisible } = this.state;

    const brandProfileEntity = getEntityFromProfile(campaign.brandProfile);

    return (
      <div className="campaign-history-card">
        <PageSection
          style="volume"
          margin="none"
          border="none"
          padding="none"
        >
          {
            !hideCcmCard && (
              <div className="campaign-history-card__logo-tooltip">
                <Tooltip
                  message={brandProfileEntity?.displayName}
                  width="short"
                  display="flex"
                >
                  <EntityAvatar
                    entity={brandProfileEntity}
                    size="customSize"
                    customSize={{
                      width: 48,
                      height: 48
                    }}
                    className="campaign-history-card__logo"
                  />
                </Tooltip>
              </div>
            )
          }
          <CampaignHistoryCardPreview
            item={item}
            disabled={disabled}
            hideCcmCard={hideCcmCard}
            onClick={this.toggleContentVisibility}
            toggleButtonDirection={isContentVisible ? 'top' : 'bottom'}
          />
          {
            isContentVisible && (
              <div className="campaign-history-card__content">
                {this.renderBindingAgreement()}
                {this.renderCampaignPerfomance()}
                {this.renderNegotiationHistory()}
                <div>
                  {this.renderInfluencerMetricInfo()}
                  {this.renderDeliverables()}
                </div>
              </div>
            )
          }
        </PageSection>
      </div>
    );
  }
}

export default CampaignHistoryCard;
